import type { AnalyticsEvent } from "@/types/models";

const TIKTOK_SCRIPT_URL = "https://analytics.tiktok.com/i18n/pixel/events.js";
const TIKTOK_EVENT_STORAGE_PREFIX = "awraq:tiktok-event:";
const sentEvents = new Set<string>();
const ACQUISITION_PATHS = new Set(["/", "/paper-tree"]);

type TikTokParameters = Record<string, string | number | boolean>;

type TikTokQueue = Array<IArguments> & {
  _i?: Array<[string, TikTokParameters, Record<string, never>]>;
  _o?: Record<string, TikTokParameters>;
  _t?: number;
  load?: (pixelId: string, options?: TikTokParameters) => void;
  page?: () => void;
  track?: (event: string, parameters?: TikTokParameters, options?: { event_id?: string }) => void;
};

type TikTokWindow = Window & {
  TiktokAnalyticsObject?: string;
  awraqTikTokPixelInitialized?: boolean;
  ttq?: TikTokQueue;
};

function addQueueMethod(queue: TikTokQueue, method: "page" | "track"): void {
  queue[method] = function queuedTikTokCall(): void {
    queue.push(arguments);
  };
}

export function initializeTikTokPixel(pixelId?: string | null): void {
  if (!pixelId || !import.meta.env.PROD) {
    return;
  }

  const tiktokWindow = window as TikTokWindow;

  if (tiktokWindow.awraqTikTokPixelInitialized) {
    return;
  }

  tiktokWindow.awraqTikTokPixelInitialized = true;
  tiktokWindow.TiktokAnalyticsObject = "ttq";
  const queue = (tiktokWindow.ttq = tiktokWindow.ttq ?? ([] as unknown as TikTokQueue));

  addQueueMethod(queue, "page");
  addQueueMethod(queue, "track");
  queue.load = (id, options = {}) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `${TIKTOK_SCRIPT_URL}?sdkid=${encodeURIComponent(id)}&lib=ttq`;
    document.head.append(script);
    queue._i = queue._i ?? [];
    queue._i.push([id, options, {}]);
    queue._t = Date.now();
    queue._o = queue._o ?? {};
    queue._o[id] = options;
  };

  queue.load(pixelId);
}

export function trackTikTokPage(pageUrl: string): void {
  const tiktokWindow = window as TikTokWindow;

  if (!tiktokWindow.awraqTikTokPixelInitialized) {
    return;
  }

  tiktokWindow.ttq?.page?.();

  const page = new URL(pageUrl, window.location.origin);

  if (ACQUISITION_PATHS.has(page.pathname)) {
    tiktokWindow.ttq?.track?.("ViewContent", {
      description: page.pathname === "/paper-tree" ? "paper_tree_landing" : "product_landing",
    });
  }
}

export function trackTikTokEventOnce(event?: AnalyticsEvent | null): void {
  if (!event || event.name !== "sign_up" || sentEvents.has(event.id)) {
    return;
  }

  const tiktokWindow = window as TikTokWindow;

  if (!tiktokWindow.awraqTikTokPixelInitialized) {
    return;
  }

  const storageKey = `${TIKTOK_EVENT_STORAGE_PREFIX}${event.id}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }
  } catch {
    // Storage may be disabled; the in-memory guard still deduplicates this visit.
  }

  tiktokWindow.ttq?.track?.(
    "CompleteRegistration",
    {
      method: event.parameters.method,
      surface: event.parameters.surface,
    },
    { event_id: event.id },
  );
  sentEvents.add(event.id);

  try {
    sessionStorage.setItem(storageKey, "1");
  } catch {
    // persistence-best-effort: the in-memory guard deduplicates; storage failure must not interrupt onboarding.
  }
}
